import PropTypes from 'prop-types';
import { Component } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Typeahead } from 'react-typeahead';
import _ from 'underscore';
import type { MouseEvent, VoidFunctionComponent } from 'react';

import { getLicense, getLicenses } from '../../api/licenses';
import { type DialogState, useOnAlert } from '../../contexts/DialogContext';
import utils from '../../utils';
import Icon, { icons, IconText } from '../generic/Icon';
import SupportLink from '../generic/Link/SupportLink';
import type { License } from '../../types';

type CustomLicenseFields = {
  title: string;
  url: string;
  text: string;
};

export type CustomLicense = { licenseId: string } & CustomLicenseFields;

type LicenseBundle = {
  license: License;
  custom: CustomLicenseFields;
};

type State = {
  combo_list: License[];
  loading: boolean;
  error: string;
} & LicenseBundle;

type Props = {
  initLicenseData?: LicenseBundle;
  revisionId?: string;
  title?: string;
  show?: boolean;
  // isTitleEditable defaults to true.
  isTitleEditable?: boolean;
  previousLicenseCount?: number; // if < 1 then there will be an alert to tell the user to run an issue scan
  onAddLicense: (customLicense: CustomLicense) => Promise<void>;
  onHide?(event?: MouseEvent<{}>): void;
  // if true, we will not show the window.alert when adding a license
  shouldSkipWarning?: boolean;
  onAlert: DialogState['onAlert'];
};

/**
 * @deprecated - To be replaced by NewModalAddLicense
 */
class ModalAddLicense extends Component<Props, State> {
  static propTypes = {
    onAddLicense: PropTypes.func,
    initLicenseData: PropTypes.object,
    title: PropTypes.string,
    show: PropTypes.bool,
    onHide: PropTypes.func,
    revisionId: PropTypes.string,
  };

  onFetchAutocomplete = _.debounce(query => {
    return getLicenses({ $title: query })
      .then(data => this.setState({ combo_list: data }))
      .catch(error => this.setState({ error }));
  }, 250);

  constructor(props) {
    super(props);
    this.state = this.getInitialState(props);
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (!prevProps.show && this.props.show) {
      this.onResetState();
    }
  }

  onResetState = () =>
    this.setState(this.getInitialState(this.props));

  onAutocompleteChange = ev => {
    void this.onFetchAutocomplete(ev.target.value);
  };

  onOptionSelected = async (option: License) => {
    const license: License = await getLicense(option.licenseId);
    this.setState({
      license,
      custom: {
        title: '',
        url: '',
        text: utils.stripHtml(license.licenseText) || '',
      },
    });
  };

  onChangeCustomData = (field, value) => {
    const customData = _.extend({}, this.state.custom);
    customData[field] = value;
    this.setState({ custom: customData });
  };

  onConfirm = () => {
    // some validation logic
    if (!this.state.license) {
      this.setState({ error: 'License must be selected' });
      return;
    }
    this.setState({ loading: true });

    const submitData = _.extend(_.pick(this.state.license, 'licenseId'), this.state.custom);
    const isFirstLicense = _.isNumber(this.props.previousLicenseCount) && this.props.previousLicenseCount < 1;

    // eslint-disable-next-line consistent-return
    return this.props.onAddLicense(submitData)
      .then(() => {
        this.setState(this.getInitialState(this.props));
        if (!this.props.shouldSkipWarning && isFirstLicense) {
          this.props.onAlert({
            title: 'Policy rescan required',
            body: 'Note: In order for this change to be reflected in your issues, you will need to re-run a policy scan.',
          });
        }
        this.props.onHide();
        return null;
      },
      err => {
        this.setState({
          loading: false,
          error: err.message,
        });
      },
      );
  };

  getInitialState(props) {
    return {
      ...(props.initLicenseData || {
        license: null,
        custom: { title: '', url: '', text: '' },
      }),
      combo_list: [],
      loading: false,
      error: null,
    };
  }

  render() {
    const isTitleEditable = this.props.isTitleEditable ?? true;

    return (
      <Modal
        bsSize="small"
        backdrop="static"
        animation
        show={this.props.show}
        onHide={this.props.onHide}
        onEscapeKeyDown={this.props.onHide}
        className="modal-horizontal"
      >
        <div className="row">
          <div className="col-md-6 modal-horizontal-header">
            {!this.state.license ? (
              <div className="padded">
                <span className="icon-feature">
                  <Icon icon={icons.addToProject} />
                </span>
                <h4>Add a License</h4>
                <br />
                <Button onClick={this.props.onHide}>Cancel</Button>&nbsp;&nbsp;
                <Button className="btn-add-license" disabled bsStyle="primary">
                  Confirm
                </Button>
              </div>
            ) : (
              <div className="padded">
                <Button bsStyle="link" onClick={this.onResetState}>
                  <IconText
                    icon={icons.arrowLeft}
                    text="Go Back"
                  />
                </Button>
                <br /><br />
                <h4>
                  {utils.trimString(
                    this.state.license.licenseId,
                    20,
                  )}
                </h4>
                <p>
                  {utils.trimString(
                    this.state.license.Summary
                      ? this.state.license.Summary.summary
                      : this.state.license.licenseComments || 'No summary found.',
                    150,
                  )}
                </p>
                <br />
                <Button onClick={this.props.onHide}>Cancel</Button>&nbsp;&nbsp;
                <Button
                  className="btn-add-license"
                  onClick={this.onConfirm}
                  disabled={this.state.loading || !this.state.license}
                  bsStyle="primary">
                  Confirm
                </Button>
              </div>
            )}
          </div>
          <div className="col-md-6 modal-horizontal-content add-license-search">
            <div className={`${this.state.loading ? 'loading loading-cruncher' : ''} padded`}>
              {this.state.license ? (
                <div>
                  {isTitleEditable && (
                    <div className="form-group">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="custom-license-title form-control"
                        onChange={(e) => {
                          this.onChangeCustomData('title', e.target.value);
                        }}
                        value={this.state.custom.title}
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">URL</label>
                    <input
                      type="text"
                      className="custom-license-url form-control"
                      onChange={(e) => {
                        this.onChangeCustomData('url', e.target.value);
                      }}
                      value={this.state.custom.url}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Text</label>
                    <textarea
                      className="custom-license-text form-control"
                      onChange={(e) => {
                        this.onChangeCustomData('text', e.target.value);
                      }}
                      value={this.state.custom.text}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label>Search Licenses by Title</label>
                  <Typeahead
                    className={`dropdown open form-group ${this.state.license ? 'has-success' : ''}`}
                    options={this.state.combo_list}
                    placeholder="i.e. MIT License"
                    onChange={this.onAutocompleteChange}
                    customClasses={{
                      results: 'dropdown-menu',
                      input: 'form-control input-lg input-license',
                      hover: 'active',
                    }}
                    inputProps={{ autoFocus: true }}
                    onOptionSelected={this.onOptionSelected}
                    filterOption={() => true}
                    displayOption={option => option.name}
                  />
                  <p className="subtitle">
                    <SupportLink>
                      Can&apos;t find a license? Contact support to request it added.
                    </SupportLink>
                  </p>
                </div>
              )}
              {this.state.error ? <div className="alert alert-error">{this.state.error}</div> : ''}
            </div>
          </div>
        </div>
      </Modal>
    );
  }
}

const ModalAddLicenseWithOnAlert: VoidFunctionComponent<Omit<Props, 'onAlert'>> = (props) => {
  const onAlert = useOnAlert();
  return (
    <ModalAddLicense
      {...props}
      onAlert={onAlert}
    />
  );
};

export default ModalAddLicenseWithOnAlert;
